import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Sprout, ArrowLeft, Save, Send, Upload, Trash2, Download,
  Loader2, CheckCircle, AlertCircle, FileText, Plus, X
} from 'lucide-react';
import {
  getOnboardingSection, saveOnboardingSection,
  uploadOnboardingDocument, deleteOnboardingDocument,
  downloadOnboardingDocument
} from '../services/api';
import { toast } from 'sonner';

const OnboardingSection = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState(null);
  const [formData, setFormData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(null); // tracks which category is uploading
  const fileInputRef = useRef(null);
  const idFileInputRef = useRef(null);
  const proofFileInputRef = useRef(null);

  const fetchSection = useCallback(async () => {
    try {
      const res = await getOnboardingSection(sectionId);
      setSectionData(res.data);
      const existingData = res.data.data || {};
      
      // Initialize default form data for sections with complex structures
      if (sectionId === 'references' && !existingData.references) {
        existingData.references = [
          { name: '', organisation: '', email: '', relationship: '' },
          { name: '', organisation: '', email: '', relationship: '' }
        ];
      }
      if (sectionId === 'employment_history' && !existingData.employment_history) {
        existingData.employment_history = [
          { employer: '', role: '', start_date: '', end_date: '', with_children: false }
        ];
      }
      
      setFormData(existingData);
      setDocuments(res.data.documents || []);
    } catch (err) {
      toast.error('Failed to load section');
    } finally {
      setLoading(false);
    }
  }, [sectionId]);

  useEffect(() => { fetchSection(); }, [fetchSection]);

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveOnboardingSection(sectionId, formData, false);
      toast.success('Progress saved');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await saveOnboardingSection(sectionId, formData, true);
      toast.success('Section submitted for review');
      navigate('/onboarding');
    } catch (err) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (e, category = 'general') => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(category);
    try {
      await uploadOnboardingDocument(sectionId, file, category);
      toast.success('Document uploaded');
      fetchSection();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (idFileInputRef.current) idFileInputRef.current.value = '';
      if (proofFileInputRef.current) proofFileInputRef.current.value = '';
    }
  };

  const handleDeleteDoc = async (fileId) => {
    try {
      await deleteOnboardingDocument(fileId);
      setDocuments(prev => prev.filter(d => d.file_id !== fileId));
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadDoc = async (fileId, filename) => {
    try {
      await downloadOnboardingDocument(fileId, filename);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  const isReadOnly = sectionData?.status === 'submitted' || sectionData?.status === 'approved';
  const sectionTitle = SECTION_TITLES[sectionId] || sectionId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0FAF7] via-white to-[#E8F8F5]">
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#A8E6CF]/30 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/onboarding')} data-testid="back-to-onboarding">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <h1 className="text-base font-bold text-[#2D4A4A]">{sectionTitle}</h1>
          </div>
          {sectionData?.status && (
            <Badge className={`text-xs ${
              sectionData.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
              sectionData.status === 'submitted' ? 'bg-amber-100 text-amber-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              {sectionData.status === 'approved' ? 'Approved' : sectionData.status === 'submitted' ? 'Under Review' : 'In Progress'}
            </Badge>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {sectionData?.admin_notes && sectionData.status !== 'approved' && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800 text-sm">
              <strong>Admin Feedback:</strong> {sectionData.admin_notes}
            </AlertDescription>
          </Alert>
        )}

        {sectionData?.status === 'approved' && (
          <Alert className="border-emerald-200 bg-emerald-50">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <AlertDescription className="text-emerald-800 text-sm">
              This section has been approved by admin.
            </AlertDescription>
          </Alert>
        )}

        <Card className="border border-[#A8E6CF]/30 bg-white/80">
          <CardContent className="p-5 space-y-4">
            {renderSectionForm(sectionId, formData, updateField, isReadOnly)}
          </CardContent>
        </Card>

        {/* Document Upload Area */}
        {SECTIONS_WITH_UPLOADS.includes(sectionId) && sectionId === 'id_verification' && (
          <Card className="border border-[#A8E6CF]/30 bg-white/80">
            <CardHeader className="pb-2 px-5 pt-4">
              <CardTitle className="text-sm text-[#2D4A4A] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#5BA4A4]" />
                Required Documents
              </CardTitle>
              <p className="text-xs text-[#5A7A7A] mt-1">You must upload both documents below before submitting.</p>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-4">
              {/* ID Document Upload */}
              <div className="p-3 rounded-lg border border-[#A8E6CF]/30 bg-[#F0FAF7]/30">
                <p className="text-xs font-semibold text-[#2D4A4A] mb-2">1. ID Document (Passport or Driving Licence)</p>
                {documents.filter(d => d.category === 'id_document').map(doc => (
                  <div key={doc.file_id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-white text-sm mb-2" data-testid={`doc-${doc.file_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#5BA4A4] shrink-0" />
                      <span className="truncate text-[#2D4A4A]">{doc.original_filename}</span>
                      <span className="text-xs text-gray-400 shrink-0">{(doc.size / 1024).toFixed(0)}KB</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(doc.file_id, doc.original_filename)} data-testid={`download-doc-${doc.file_id}`}>
                        <Download className="w-3.5 h-3.5 text-[#5BA4A4]" />
                      </Button>
                      {!isReadOnly && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteDoc(doc.file_id)} data-testid={`delete-doc-${doc.file_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {!isReadOnly && (
                  <div>
                    <input type="file" ref={idFileInputRef} onChange={(e) => handleUpload(e, 'id_document')} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => idFileInputRef.current?.click()}
                      disabled={uploading === 'id_document'}
                      className="border-dashed border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]"
                      data-testid="upload-id-document-btn"
                    >
                      {uploading === 'id_document' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      Upload ID Document
                    </Button>
                  </div>
                )}
              </div>

              {/* Proof of Address Upload */}
              <div className="p-3 rounded-lg border border-[#A8E6CF]/30 bg-[#F0FAF7]/30">
                <p className="text-xs font-semibold text-[#2D4A4A] mb-2">2. Proof of Address (Utility bill or bank statement — dated within last 3 months)</p>
                {documents.filter(d => d.category === 'proof_of_address').map(doc => (
                  <div key={doc.file_id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-white text-sm mb-2" data-testid={`doc-${doc.file_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-[#5BA4A4] shrink-0" />
                      <span className="truncate text-[#2D4A4A]">{doc.original_filename}</span>
                      <span className="text-xs text-gray-400 shrink-0">{(doc.size / 1024).toFixed(0)}KB</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(doc.file_id, doc.original_filename)} data-testid={`download-doc-${doc.file_id}`}>
                        <Download className="w-3.5 h-3.5 text-[#5BA4A4]" />
                      </Button>
                      {!isReadOnly && (
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteDoc(doc.file_id)} data-testid={`delete-doc-${doc.file_id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {!isReadOnly && (
                  <div>
                    <input type="file" ref={proofFileInputRef} onChange={(e) => handleUpload(e, 'proof_of_address')} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => proofFileInputRef.current?.click()}
                      disabled={uploading === 'proof_of_address'}
                      className="border-dashed border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]"
                      data-testid="upload-proof-address-btn"
                    >
                      {uploading === 'proof_of_address' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      Upload Proof of Address
                    </Button>
                  </div>
                )}
              </div>

              {/* Show any legacy/uncategorized docs */}
              {documents.filter(d => d.category !== 'id_document' && d.category !== 'proof_of_address').length > 0 && (
                <div>
                  <p className="text-xs text-[#5A7A7A] mb-1">Other Documents:</p>
                  {documents.filter(d => d.category !== 'id_document' && d.category !== 'proof_of_address').map(doc => (
                    <div key={doc.file_id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm mb-1" data-testid={`doc-${doc.file_id}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-[#5BA4A4] shrink-0" />
                        <span className="truncate text-[#2D4A4A]">{doc.original_filename}</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(doc.file_id, doc.original_filename)}>
                          <Download className="w-3.5 h-3.5 text-[#5BA4A4]" />
                        </Button>
                        {!isReadOnly && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteDoc(doc.file_id)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Generic Document Upload Area (for non-id_verification sections) */}
        {SECTIONS_WITH_UPLOADS.includes(sectionId) && sectionId !== 'id_verification' && (
          <Card className="border border-[#A8E6CF]/30 bg-white/80">
            <CardHeader className="pb-2 px-5 pt-4">
              <CardTitle className="text-sm text-[#2D4A4A] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#5BA4A4]" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-3">
              {documents.map(doc => (
                <div key={doc.file_id} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-200 bg-gray-50 text-sm" data-testid={`doc-${doc.file_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-[#5BA4A4] shrink-0" />
                    <span className="truncate text-[#2D4A4A]">{doc.original_filename}</span>
                    <span className="text-xs text-gray-400 shrink-0">{(doc.size / 1024).toFixed(0)}KB</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDownloadDoc(doc.file_id, doc.original_filename)} data-testid={`download-doc-${doc.file_id}`}>
                      <Download className="w-3.5 h-3.5 text-[#5BA4A4]" />
                    </Button>
                    {!isReadOnly && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteDoc(doc.file_id)} data-testid={`delete-doc-${doc.file_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {!isReadOnly && (
                <div>
                  <input type="file" ref={fileInputRef} onChange={(e) => handleUpload(e, 'general')} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!!uploading}
                    className="border-dashed border-[#A8E6CF] text-[#5BA4A4] hover:bg-[#F0FAF7]"
                    data-testid="upload-document-btn"
                  >
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Upload Document
                  </Button>
                  <p className="text-[10px] text-gray-400 mt-1">PDF, JPG, PNG, DOC — Max 10MB</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {!isReadOnly && (
          <div className="flex gap-3 justify-end pb-8">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={saving}
              className="border-[#A8E6CF] text-[#5BA4A4]"
              data-testid="save-progress-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save Progress
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#5BA4A4] hover:bg-[#4A9090] text-white"
              data-testid="submit-section-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Submit for Review
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

const SECTION_TITLES = {
  personal_details: 'Personal Details',
  id_verification: 'Identity Verification',
  right_to_work: 'Right to Work',
  dbs_check: 'DBS Check',
  references: 'References',
  employment_history: 'Employment History',
  education: 'Education & Qualifications',
  safeguarding_declaration: 'Safeguarding Declaration',
  criminal_declaration: 'Criminal Self-Declaration',
  health_declaration: 'Health Declaration',
  policies: 'Policies & Agreements',
  bank_details: 'Bank Details',
};

const SECTIONS_WITH_UPLOADS = [
  'id_verification', 'right_to_work', 'dbs_check', 'references', 'education'
];

const Field = ({ label, id, value, onChange, type = 'text', placeholder, required, disabled, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-medium text-[#2D4A4A]">{label} {required && <span className="text-red-400">*</span>}</Label>
    {children || (
      <Input
        id={id}
        type={type}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] text-sm h-9"
        data-testid={`field-${id}`}
      />
    )}
  </div>
);

const TextArea = ({ label, id, value, onChange, placeholder, disabled, rows = 3 }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs font-medium text-[#2D4A4A]">{label}</Label>
    <Textarea
      id={id}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className="border-[#A8E6CF]/50 focus:border-[#5BA4A4] text-sm"
      data-testid={`field-${id}`}
    />
  </div>
);

const CheckField = ({ label, id, checked, onChange, disabled }) => (
  <label className="flex items-start gap-2 cursor-pointer" data-testid={`check-${id}`}>
    <input type="checkbox" checked={checked || false} onChange={e => onChange(e.target.checked)} disabled={disabled} className="mt-0.5 accent-[#5BA4A4]" />
    <span className="text-xs text-[#2D4A4A]">{label}</span>
  </label>
);

function renderSectionForm(sectionId, data, update, disabled) {
  switch (sectionId) {
    case 'personal_details':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A] mb-2">Please provide your full legal details as they appear on your official documents.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full Legal Name" id="full_name" value={data.full_name} onChange={v => update('full_name', v)} placeholder="As per passport/ID" required disabled={disabled} />
            <Field label="Date of Birth" id="date_of_birth" value={data.date_of_birth} onChange={v => update('date_of_birth', v)} type="date" required disabled={disabled} />
          </div>
          <Field label="Address" id="address" value={data.address} onChange={v => update('address', v)} placeholder="Full address including house number" required disabled={disabled} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Postcode" id="postcode" value={data.postcode} onChange={v => update('postcode', v)} placeholder="e.g. SW1A 1AA" required disabled={disabled} />
            <Field label="Contact Number" id="phone" value={data.phone} onChange={v => update('phone', v)} placeholder="Mobile or landline" required disabled={disabled} />
          </div>
          <Field label="Email Address" id="email" value={data.email} onChange={v => update('email', v)} type="email" required disabled={disabled} />
          <Field label="National Insurance Number" id="national_insurance" value={data.national_insurance} onChange={v => update('national_insurance', v)} placeholder="e.g. QQ 12 34 56 C" required disabled={disabled} />
          <hr className="border-[#A8E6CF]/30" />
          <p className="text-xs font-medium text-[#2D4A4A]">Emergency Contact</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Emergency Contact Name" id="emergency_name" value={data.emergency_name} onChange={v => update('emergency_name', v)} required disabled={disabled} />
            <Field label="Emergency Contact Number" id="emergency_phone" value={data.emergency_phone} onChange={v => update('emergency_phone', v)} required disabled={disabled} />
          </div>
          <Field label="Relationship to You" id="emergency_relationship" value={data.emergency_relationship} onChange={v => update('emergency_relationship', v)} disabled={disabled} />
        </div>
      );

    case 'id_verification':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Upload a copy of your passport or driving licence, and a proof of address (utility bill or bank statement dated within the last 3 months).</p>
          <Field label="ID Type" id="id_type" value={data.id_type} onChange={v => update('id_type', v)} disabled={disabled}>
            <select id="id_type" value={data.id_type || ''} onChange={e => update('id_type', e.target.value)} disabled={disabled} className="w-full h-9 border border-[#A8E6CF]/50 rounded-md px-3 text-sm bg-white" data-testid="field-id_type">
              <option value="">Select ID type</option>
              <option value="passport">Passport</option>
              <option value="driving_licence">Driving Licence</option>
            </select>
          </Field>
          <Field label="ID Number" id="id_number" value={data.id_number} onChange={v => update('id_number', v)} disabled={disabled} />
          <Field label="ID Expiry Date" id="id_expiry" value={data.id_expiry} onChange={v => update('id_expiry', v)} type="date" disabled={disabled} />
          <p className="text-xs text-gray-500 mt-2">Please upload your ID and proof of address documents below.</p>
        </div>
      );

    case 'right_to_work':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Under UK law, we must verify your right to work in the United Kingdom before you can start.</p>
          <Field label="Right to Work Status" id="rtw_status" value={data.rtw_status} onChange={v => update('rtw_status', v)} disabled={disabled}>
            <select id="rtw_status" value={data.rtw_status || ''} onChange={e => update('rtw_status', e.target.value)} disabled={disabled} className="w-full h-9 border border-[#A8E6CF]/50 rounded-md px-3 text-sm bg-white" data-testid="field-rtw_status">
              <option value="">Select status</option>
              <option value="british_citizen">British Citizen</option>
              <option value="settled_status">Settled Status (EU)</option>
              <option value="pre_settled">Pre-Settled Status (EU)</option>
              <option value="visa">Work Visa</option>
              <option value="other">Other</option>
            </select>
          </Field>
          {(data.rtw_status === 'visa' || data.rtw_status === 'pre_settled') && (
            <Field label="Expiry Date" id="rtw_expiry" value={data.rtw_expiry} onChange={v => update('rtw_expiry', v)} type="date" disabled={disabled} />
          )}
          <Field label="Share Code (if applicable)" id="rtw_share_code" value={data.rtw_share_code} onChange={v => update('rtw_share_code', v)} placeholder="e.g. from gov.uk share code service" disabled={disabled} />
          <p className="text-xs text-gray-500 mt-2">Please upload supporting documents below.</p>
        </div>
      );

    case 'dbs_check':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">An Enhanced DBS check is required for all staff working with children. If you already have one, please provide the details below.</p>
          <Field label="DBS Status" id="dbs_status" value={data.dbs_status} onChange={v => update('dbs_status', v)} disabled={disabled}>
            <select id="dbs_status" value={data.dbs_status || ''} onChange={e => update('dbs_status', e.target.value)} disabled={disabled} className="w-full h-9 border border-[#A8E6CF]/50 rounded-md px-3 text-sm bg-white" data-testid="field-dbs_status">
              <option value="">Select status</option>
              <option value="not_started">Not Started</option>
              <option value="in_progress">Application In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </Field>
          {data.dbs_status === 'completed' && (
            <>
              <Field label="DBS Certificate Number" id="dbs_number" value={data.dbs_number} onChange={v => update('dbs_number', v)} required disabled={disabled} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Issue Date" id="dbs_issue_date" value={data.dbs_issue_date} onChange={v => update('dbs_issue_date', v)} type="date" required disabled={disabled} />
                <Field label="DBS Type" id="dbs_type" value={data.dbs_type} onChange={v => update('dbs_type', v)} disabled={disabled}>
                  <select id="dbs_type" value={data.dbs_type || ''} onChange={e => update('dbs_type', e.target.value)} disabled={disabled} className="w-full h-9 border border-[#A8E6CF]/50 rounded-md px-3 text-sm bg-white" data-testid="field-dbs_type">
                    <option value="">Select type</option>
                    <option value="enhanced">Enhanced</option>
                    <option value="enhanced_barred">Enhanced with Barred List</option>
                  </select>
                </Field>
              </div>
              <CheckField label="Registered with DBS Update Service" id="dbs_update_service" checked={data.dbs_update_service} onChange={v => update('dbs_update_service', v)} disabled={disabled} />
            </>
          )}
          <p className="text-xs text-gray-500 mt-2">Please upload your DBS certificate below if available.</p>
        </div>
      );

    case 'references':
      return <ReferencesForm data={data} update={update} disabled={disabled} />;

    case 'employment_history':
      return <EmploymentHistoryForm data={data} update={update} disabled={disabled} />;

    case 'education':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Please provide your educational qualifications and any relevant teaching/tutoring experience.</p>
          <Field label="Highest Qualification" id="highest_qual" value={data.highest_qual} onChange={v => update('highest_qual', v)} placeholder="e.g. BSc Mathematics, PGCE" disabled={disabled} />
          <Field label="Institution" id="qual_institution" value={data.qual_institution} onChange={v => update('qual_institution', v)} disabled={disabled} />
          <Field label="Year Completed" id="qual_year" value={data.qual_year} onChange={v => update('qual_year', v)} type="number" placeholder="e.g. 2020" disabled={disabled} />
          <TextArea label="Relevant Subject Qualifications" id="subject_quals" value={data.subject_quals} onChange={v => update('subject_quals', v)} placeholder="List any subject-specific qualifications (e.g. A-Level Maths, GCSE English)" disabled={disabled} />
          <TextArea label="Teaching / Tutoring Experience" id="teaching_exp" value={data.teaching_exp} onChange={v => update('teaching_exp', v)} placeholder="Describe your experience working with students" disabled={disabled} />
          <p className="text-xs text-gray-500 mt-2">Please upload copies of certificates below.</p>
        </div>
      );

    case 'safeguarding_declaration':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">As part of our safer recruitment process, you must confirm the following declarations.</p>
          <div className="space-y-2 bg-[#F0FAF7] p-3 rounded-lg">
            <CheckField label="I confirm that I have no history of safeguarding concerns raised against me." id="no_safeguarding_concerns" checked={data.no_safeguarding_concerns} onChange={v => update('no_safeguarding_concerns', v)} disabled={disabled} />
            <CheckField label="I confirm that I am not disqualified from working with children under any legislation." id="not_disqualified" checked={data.not_disqualified} onChange={v => update('not_disqualified', v)} disabled={disabled} />
            <CheckField label="I agree to uphold safeguarding responsibilities and report any concerns immediately." id="agree_safeguarding" checked={data.agree_safeguarding} onChange={v => update('agree_safeguarding', v)} disabled={disabled} />
          </div>
          <hr className="border-[#A8E6CF]/30" />
          <Field label="Full Name (Digital Signature)" id="sig_name" value={data.sig_name} onChange={v => update('sig_name', v)} placeholder="Type your full legal name" required disabled={disabled} />
          <Field label="Date" id="sig_date" value={data.sig_date} onChange={v => update('sig_date', v)} type="date" required disabled={disabled} />
        </div>
      );

    case 'criminal_declaration':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Before your DBS check is processed, you must declare any unspent convictions, cautions, reprimands or warnings.</p>
          <div className="space-y-2 bg-[#F0FAF7] p-3 rounded-lg">
            <CheckField label="I declare that I have no unspent convictions, cautions, reprimands or warnings." id="no_convictions" checked={data.no_convictions} onChange={v => update('no_convictions', v)} disabled={disabled} />
          </div>
          {!data.no_convictions && (
            <TextArea label="Please provide details of any convictions, cautions, reprimands or warnings" id="conviction_details" value={data.conviction_details} onChange={v => update('conviction_details', v)} placeholder="Provide full details including dates" disabled={disabled} />
          )}
          <div className="space-y-2 bg-[#F0FAF7] p-3 rounded-lg">
            <CheckField label="I confirm that the information above is true and complete. I understand that providing false information may result in dismissal." id="honesty_agreement" checked={data.honesty_agreement} onChange={v => update('honesty_agreement', v)} disabled={disabled} />
          </div>
          <Field label="Full Name (Digital Signature)" id="criminal_sig_name" value={data.criminal_sig_name} onChange={v => update('criminal_sig_name', v)} placeholder="Type your full legal name" required disabled={disabled} />
          <Field label="Date" id="criminal_sig_date" value={data.criminal_sig_date} onChange={v => update('criminal_sig_date', v)} type="date" required disabled={disabled} />
        </div>
      );

    case 'health_declaration':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Please declare any health conditions that may affect your ability to carry out this role.</p>
          <Field label="Do you have any conditions that may impact your role?" id="has_conditions" value={data.has_conditions} onChange={v => update('has_conditions', v)} disabled={disabled}>
            <select id="has_conditions" value={data.has_conditions || ''} onChange={e => update('has_conditions', e.target.value)} disabled={disabled} className="w-full h-9 border border-[#A8E6CF]/50 rounded-md px-3 text-sm bg-white" data-testid="field-has_conditions">
              <option value="">Select</option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Field>
          {data.has_conditions === 'yes' && (
            <TextArea label="Please provide details" id="condition_details" value={data.condition_details} onChange={v => update('condition_details', v)} disabled={disabled} />
          )}
          <CheckField label="I confirm I am fit to work with children and young people." id="fit_to_work" checked={data.fit_to_work} onChange={v => update('fit_to_work', v)} disabled={disabled} />
          <TextArea label="Any adjustments required?" id="adjustments" value={data.adjustments} onChange={v => update('adjustments', v)} placeholder="Leave blank if none required" disabled={disabled} />
        </div>
      );

    case 'policies':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Please read the following policies on our website and confirm you agree to abide by them.</p>
          <div className="bg-[#F0FAF7] p-3 rounded-lg space-y-2">
            <a href="https://www.growingmindstuitionservices.co.uk" target="_blank" rel="noopener noreferrer" className="text-sm text-[#5BA4A4] underline hover:text-[#4A9090] block" data-testid="policies-website-link">
              View all policies on growingmindstuitionservices.co.uk
            </a>
          </div>
          <div className="space-y-2 mt-3">
            <CheckField label="I have read and agree to the Safeguarding Policy" id="agree_safeguarding_policy" checked={data.agree_safeguarding_policy} onChange={v => update('agree_safeguarding_policy', v)} disabled={disabled} />
            <CheckField label="I have read and agree to the Code of Conduct" id="agree_code_of_conduct" checked={data.agree_code_of_conduct} onChange={v => update('agree_code_of_conduct', v)} disabled={disabled} />
            <CheckField label="I have read and agree to the Health and Safety Policy" id="agree_health_safety" checked={data.agree_health_safety} onChange={v => update('agree_health_safety', v)} disabled={disabled} />
            <CheckField label="I have read and agree to the Data Protection Policy" id="agree_data_protection" checked={data.agree_data_protection} onChange={v => update('agree_data_protection', v)} disabled={disabled} />
          </div>
          <hr className="border-[#A8E6CF]/30" />
          <Field label="Full Name (Digital Signature)" id="policy_sig_name" value={data.policy_sig_name} onChange={v => update('policy_sig_name', v)} placeholder="Type your full legal name" required disabled={disabled} />
          <Field label="Date" id="policy_sig_date" value={data.policy_sig_date} onChange={v => update('policy_sig_date', v)} type="date" required disabled={disabled} />
        </div>
      );

    case 'bank_details':
      return (
        <div className="space-y-3">
          <p className="text-xs text-[#5A7A7A]">Your bank details are required for payroll processing and are stored securely.</p>
          <Field label="Account Holder Name" id="account_name" value={data.account_name} onChange={v => update('account_name', v)} required disabled={disabled} />
          <Field label="Bank Name" id="bank_name" value={data.bank_name} onChange={v => update('bank_name', v)} placeholder="e.g. Barclays, HSBC, Lloyds" required disabled={disabled} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Sort Code" id="sort_code" value={data.sort_code} onChange={v => update('sort_code', v)} placeholder="e.g. 12-34-56" required disabled={disabled} />
            <Field label="Account Number" id="account_number" value={data.account_number} onChange={v => update('account_number', v)} placeholder="8 digits" required disabled={disabled} />
          </div>
        </div>
      );

    default:
      return <p className="text-sm text-gray-500">Section form not found.</p>;
  }
}

// References sub-form (allows adding multiple)
function ReferencesForm({ data, update, disabled }) {
  const refs = data.references || [{ name: '', organisation: '', email: '', relationship: '' }, { name: '', organisation: '', email: '', relationship: '' }];

  const updateRef = (idx, key, val) => {
    const newRefs = [...refs];
    newRefs[idx] = { ...newRefs[idx], [key]: val };
    update('references', newRefs);
  };

  const addRef = () => update('references', [...refs, { name: '', organisation: '', email: '', relationship: '' }]);
  const removeRef = (idx) => {
    if (refs.length <= 2) return;
    update('references', refs.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-[#5A7A7A]">Provide at least 2 references. One must be your most recent employer. References will be contacted as part of our safer recruitment process.</p>
      {refs.map((ref, idx) => (
        <div key={idx} className="p-3 rounded-lg border border-[#A8E6CF]/30 bg-[#F0FAF7]/30 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#2D4A4A]">Reference {idx + 1} {idx === 0 && '(Most recent employer)'}</span>
            {idx >= 2 && !disabled && (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeRef(idx)}><X className="w-3 h-3" /></Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Referee Name" id={`ref_name_${idx}`} value={ref.name} onChange={v => updateRef(idx, 'name', v)} disabled={disabled} />
            <Field label="Organisation" id={`ref_org_${idx}`} value={ref.organisation} onChange={v => updateRef(idx, 'organisation', v)} disabled={disabled} />
            <Field label="Contact Email" id={`ref_email_${idx}`} value={ref.email} onChange={v => updateRef(idx, 'email', v)} type="email" disabled={disabled} />
            <Field label="Relationship" id={`ref_rel_${idx}`} value={ref.relationship} onChange={v => updateRef(idx, 'relationship', v)} placeholder="e.g. Line Manager" disabled={disabled} />
          </div>
        </div>
      ))}
      {!disabled && (
        <Button variant="outline" size="sm" onClick={addRef} className="border-[#A8E6CF] text-[#5BA4A4]">
          <Plus className="w-3 h-3 mr-1" /> Add Reference
        </Button>
      )}
    </div>
  );
}

// Employment History sub-form
function EmploymentHistoryForm({ data, update, disabled }) {
  const jobs = data.employment_history || [{ employer: '', role: '', start_date: '', end_date: '', with_children: false }];

  const updateJob = (idx, key, val) => {
    const newJobs = [...jobs];
    newJobs[idx] = { ...newJobs[idx], [key]: val };
    update('employment_history', newJobs);
  };

  const addJob = () => update('employment_history', [...jobs, { employer: '', role: '', start_date: '', end_date: '', with_children: false }]);
  const removeJob = (idx) => {
    if (jobs.length <= 1) return;
    update('employment_history', jobs.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-[#5A7A7A]">Please provide your full employment history for the last 5 years. Include any gaps and explain them.</p>
      {jobs.map((job, idx) => (
        <div key={idx} className="p-3 rounded-lg border border-[#A8E6CF]/30 bg-[#F0FAF7]/30 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-[#2D4A4A]">Position {idx + 1}</span>
            {idx >= 1 && !disabled && (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeJob(idx)}><X className="w-3 h-3" /></Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Employer" id={`emp_name_${idx}`} value={job.employer} onChange={v => updateJob(idx, 'employer', v)} disabled={disabled} />
            <Field label="Job Title" id={`emp_role_${idx}`} value={job.role} onChange={v => updateJob(idx, 'role', v)} disabled={disabled} />
            <Field label="Start Date" id={`emp_start_${idx}`} value={job.start_date} onChange={v => updateJob(idx, 'start_date', v)} type="date" disabled={disabled} />
            <Field label="End Date" id={`emp_end_${idx}`} value={job.end_date} onChange={v => updateJob(idx, 'end_date', v)} type="date" placeholder="Leave blank if current" disabled={disabled} />
          </div>
          <CheckField label="This role involved working with children" id={`emp_children_${idx}`} checked={job.with_children} onChange={v => updateJob(idx, 'with_children', v)} disabled={disabled} />
        </div>
      ))}
      {!disabled && (
        <Button variant="outline" size="sm" onClick={addJob} className="border-[#A8E6CF] text-[#5BA4A4]">
          <Plus className="w-3 h-3 mr-1" /> Add Position
        </Button>
      )}
      <TextArea label="Explain any gaps in employment" id="employment_gaps" value={data.employment_gaps} onChange={v => update('employment_gaps', v)} placeholder="If there are no gaps, write 'None'" disabled={disabled} />
    </div>
  );
}

export default OnboardingSection;
